import React, { useState, useEffect } from 'react';
import { Trash2, ArrowUpRight, ArrowDownRight, Plus, DollarSign } from 'lucide-react';

const apiBaseUrl = process.env.REACT_APP_API_URL || '';

const FinanceiroModule = ({ darkMode }) => {
  const [transacoes, setTransacoes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    tipo: 'entrada', descricao: '', valor: '',
    data: new Date().toISOString().split('T')[0], categoria: 'Honorários'
  });

  const carregar = async () => {
    try {
      const res = await fetch(`${apiBaseUrl}/api/financeiro`);
      const data = await res.json();
      setTransacoes(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error('Erro ao carregar financeiro:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { carregar(); }, []);

  const adicionar = async (e) => {
    e.preventDefault();
    try {
      await fetch(`${apiBaseUrl}/api/financeiro`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, valor: parseFloat(form.valor) }),
      });
      setForm({ tipo: 'entrada', descricao: '', valor: '', data: new Date().toISOString().split('T')[0], categoria: 'Honorários' });
      setShowForm(false);
      carregar();
    } catch (e) {
      console.error('Erro ao salvar:', e);
    }
  };

  const excluir = async (id) => {
    if (!window.confirm('Excluir este lançamento?')) return;
    await fetch(`${apiBaseUrl}/api/financeiro/${id}`, { method: 'DELETE' });
    carregar();
  };

  const entradas = transacoes.filter(t => t.tipo === 'entrada').reduce((s, t) => s + Number(t.valor), 0);
  const saidas   = transacoes.filter(t => t.tipo === 'saida').reduce((s, t) => s + Number(t.valor), 0);
  const saldo    = entradas - saidas;
  const fmt = (v) => Number(v).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

  const card  = darkMode ? 'bg-[#161b27] border border-[#1e293b]' : 'bg-white border border-slate-200';
  const input = darkMode
    ? 'bg-[#1e2537] border border-[#2d3748] text-slate-200 placeholder-slate-500'
    : 'bg-slate-50 border border-slate-200 text-slate-800';
  const label = darkMode ? 'text-slate-400' : 'text-slate-500';
  const row   = darkMode ? 'border-[#1e293b]' : 'border-slate-100';
  const txt   = darkMode ? 'text-slate-200' : 'text-slate-800';
  const sub   = darkMode ? 'text-slate-500' : 'text-slate-400';

  return (
    <div className="space-y-6 animate-in fade-in duration-500">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className={`text-2xl font-black tracking-tight ${txt}`}>Financeiro</h2>
          <p className={`text-sm font-medium ${sub}`}>Controle de entradas e saídas do escritório.</p>
        </div>
        <button
          onClick={() => setShowForm(!showForm)}
          className="flex items-center gap-2 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-sm font-bold shadow-md transition-all"
        >
          <Plus size={18} /> Novo Lançamento
        </button>
      </div>

      {/* Cards de resumo */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {[
          { label: 'Entradas', valor: entradas, color: 'text-emerald-500', border: 'border-l-4 border-emerald-500', icon: <ArrowUpRight size={20} className="text-emerald-500" /> },
          { label: 'Saídas',   valor: saidas,   color: 'text-red-500',     border: 'border-l-4 border-red-500',     icon: <ArrowDownRight size={20} className="text-red-500" /> },
          { label: 'Saldo',    valor: saldo,    color: saldo >= 0 ? 'text-blue-500' : 'text-red-500', border: `border-l-4 ${saldo >= 0 ? 'border-blue-500' : 'border-red-500'}`, icon: <DollarSign size={20} className={saldo >= 0 ? 'text-blue-500' : 'text-red-500'} /> },
        ].map((c, i) => (
          <div key={i} className={`${card} ${c.border} rounded-2xl p-6 shadow-sm`}>
            <div className="flex items-center justify-between mb-3">
              <span className={`text-xs font-black uppercase tracking-wider ${label}`}>{c.label}</span>
              {c.icon}
            </div>
            <p className={`text-2xl font-black ${c.color}`}>{fmt(c.valor)}</p>
          </div>
        ))}
      </div>

      {/* Formulário */}
      {showForm && (
        <div className={`${card} rounded-2xl p-6 shadow-sm`}>
          <h3 className={`text-base font-bold mb-5 ${txt}`}>Novo Lançamento</h3>
          <form onSubmit={adicionar} className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1">
              <label className={`text-xs font-bold uppercase tracking-wider ${label}`}>Tipo</label>
              <select value={form.tipo} onChange={e => setForm({ ...form, tipo: e.target.value })}
                className={`w-full p-3 rounded-xl text-sm outline-none focus:ring-2 focus:ring-blue-500/20 ${input}`}>
                <option value="entrada">Entrada</option>
                <option value="saida">Saída</option>
              </select>
            </div>
            <div className="space-y-1">
              <label className={`text-xs font-bold uppercase tracking-wider ${label}`}>Categoria</label>
              <select value={form.categoria} onChange={e => setForm({ ...form, categoria: e.target.value })}
                className={`w-full p-3 rounded-xl text-sm outline-none focus:ring-2 focus:ring-blue-500/20 ${input}`}>
                {['Honorários','Custas','Despesas','Depósito','Outros'].map(c => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div className="space-y-1 md:col-span-2">
              <label className={`text-xs font-bold uppercase tracking-wider ${label}`}>Descrição</label>
              <input value={form.descricao} onChange={e => setForm({ ...form, descricao: e.target.value })}
                required placeholder="Ex: Honorários processo 001..."
                className={`w-full p-3 rounded-xl text-sm outline-none focus:ring-2 focus:ring-blue-500/20 ${input}`} />
            </div>
            <div className="space-y-1">
              <label className={`text-xs font-bold uppercase tracking-wider ${label}`}>Valor (R$)</label>
              <input type="number" step="0.01" min="0" value={form.valor}
                onChange={e => setForm({ ...form, valor: e.target.value })}
                required placeholder="0,00"
                className={`w-full p-3 rounded-xl text-sm outline-none focus:ring-2 focus:ring-blue-500/20 ${input}`} />
            </div>
            <div className="space-y-1">
              <label className={`text-xs font-bold uppercase tracking-wider ${label}`}>Data</label>
              <input type="date" value={form.data} onChange={e => setForm({ ...form, data: e.target.value })}
                required className={`w-full p-3 rounded-xl text-sm outline-none focus:ring-2 focus:ring-blue-500/20 ${input}`} />
            </div>
            <div className="md:col-span-2 flex gap-3 justify-end pt-2">
              <button type="button" onClick={() => setShowForm(false)}
                className={`px-5 py-2.5 rounded-xl text-sm font-bold border transition-all ${darkMode ? 'border-[#2d3748] text-slate-300 hover:bg-[#1e293b]' : 'border-slate-200 text-slate-600 hover:bg-slate-50'}`}>
                Cancelar
              </button>
              <button type="submit"
                className="px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-sm font-bold transition-all">
                Salvar
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Lista de transações */}
      <div className={`${card} rounded-2xl shadow-sm overflow-hidden`}>
        {loading ? (
          <div className={`p-10 text-center text-sm ${sub}`}>Carregando...</div>
        ) : transacoes.length === 0 ? (
          <div className={`p-10 text-center text-sm ${sub}`}>
            <DollarSign size={36} className="mx-auto mb-3 opacity-30" />
            <p>Nenhum lançamento encontrado.</p>
            <p className="text-xs mt-1">Clique em "Novo Lançamento" para começar.</p>
          </div>
        ) : (
          transacoes.map((t, i) => (
            <div key={t.id}
              className={`flex items-center px-6 py-4 ${i < transacoes.length - 1 ? `border-b ${row}` : ''} transition-colors ${darkMode ? 'hover:bg-[#1e293b]' : 'hover:bg-slate-50'}`}>
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 mr-4 ${t.tipo === 'entrada' ? 'bg-emerald-500/10' : 'bg-red-500/10'}`}>
                {t.tipo === 'entrada'
                  ? <ArrowUpRight size={18} className="text-emerald-500" />
                  : <ArrowDownRight size={18} className="text-red-500" />}
              </div>
              <div className="flex-1 min-w-0">
                <p className={`text-sm font-bold truncate ${txt}`}>{t.descricao}</p>
                <p className={`text-xs mt-0.5 ${sub}`}>{t.categoria} · {t.data}</p>
              </div>
              <p className={`text-sm font-black mr-4 ${t.tipo === 'entrada' ? 'text-emerald-500' : 'text-red-500'}`}>
                {t.tipo === 'saida' ? '-' : '+'}{fmt(t.valor)}
              </p>
              <button onClick={() => excluir(t.id)}
                className={`p-2 rounded-lg transition-all ${darkMode ? 'text-slate-600 hover:text-red-400 hover:bg-red-500/10' : 'text-slate-300 hover:text-red-500 hover:bg-red-50'}`}>
                <Trash2 size={15} />
              </button>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default FinanceiroModule;
